import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { BRANCH_META, BRANCH_ORDER } from "@/lib/branches";
import { motion } from "framer-motion";
import { Search, Plus, Hammer, Users, ArrowRight, Rocket, Globe } from "lucide-react";

export default function ProjectsBrowse() {
    const [projects, setProjects] = useState([]);
    const [q, setQ] = useState("");
    const [branchFilter, setBranchFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [disciplines, setDisciplines] = useState([]);

    useEffect(() => {
        api.get("/atlas/disciplines").then((r) => setDisciplines(r.data));
    }, []);

    const disciplineMeta = useMemo(() => {
        const m = {};
        disciplines.forEach((d) => (m[d.slug] = d));
        return m;
    }, [disciplines]);

    const search = async (queryOverride = null) => {
        setLoading(true);
        const params = new URLSearchParams();
        const qv = queryOverride !== null ? queryOverride : q;
        if (qv.trim()) params.set("q", qv.trim());
        if (branchFilter) params.set("branch", branchFilter);
        params.set("limit", "60");
        try {
            const { data } = await api.get(`/projects/search?${params}`);
            setProjects(data.projects || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchFilter]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        search();
    };

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-6xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="crystal rounded-3xl p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <p className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                                <Rocket className="w-3 h-3" /> Project directory
                            </p>
                            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none" data-testid="projects-title">
                                Every project. Zero silos.
                            </h1>
                            <p className="text-slate-600 mt-2 max-w-2xl">
                                Search everything the community is building. Post yours to find collaborators
                                — the AI will connect you with anyone working on the same problem.
                            </p>
                        </div>
                        <Link to="/projects/new" className="arcade-btn" data-variant="primary" data-testid="post-project-cta">
                            <Plus className="w-4 h-4" /> Post a project
                        </Link>
                    </div>
                    <div className="mt-3">
                        <Link to="/bounties" className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-humanities hover:text-slate-900" data-testid="projects-see-bounties">
                            Or browse open bounties <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>

                    <form onSubmit={onSearchSubmit} className="mt-6 flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 rounded-2xl border border-slate-200 bg-white flex items-center gap-2 px-4 py-2.5">
                            <Search className="w-4 h-4 text-slate-400" />
                            <input
                                className="bg-transparent outline-none w-full text-sm font-sans"
                                placeholder="Search title, summary, org, tags…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                data-testid="projects-search"
                            />
                        </div>
                        <button className="arcade-btn" data-variant="ghost" data-testid="projects-search-submit">
                            <Search className="w-4 h-4" /> Search
                        </button>
                    </form>

                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <button
                            onClick={() => setBranchFilter(null)}
                            className={`px-3 py-1.5 rounded-full text-xs font-display font-bold transition-all ${!branchFilter ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white"}`}
                            data-testid="projects-filter-all"
                        >
                            All branches
                        </button>
                        {BRANCH_ORDER.map((b) => {
                            const meta = BRANCH_META[b];
                            const active = branchFilter === b;
                            return (
                                <button
                                    key={b}
                                    onClick={() => setBranchFilter(active ? null : b)}
                                    className="px-3 py-1.5 rounded-full text-xs font-display font-bold transition-all flex items-center gap-1.5"
                                    style={{
                                        background: active ? meta.color : meta.soft,
                                        color: active ? (b === "arts" ? "#0F172A" : "#fff") : meta.text,
                                    }}
                                    data-testid={`projects-filter-${b}`}
                                >
                                    <span className="w-2 h-2 rounded-full" style={{ background: active ? "#fff" : meta.color }} />
                                    {meta.name}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>

                <div className="mt-8">
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">
                        {loading ? "Searching…" : `${projects.length} project${projects.length === 1 ? "" : "s"} found`}
                    </div>
                    {projects.length === 0 && !loading && (
                        <div className="crystal rounded-3xl p-10 text-center">
                            <Hammer className="w-8 h-8 mx-auto text-slate-400" />
                            <h2 className="font-display font-bold text-2xl tracking-tighter text-slate-900 mt-3">
                                No projects found.
                            </h2>
                            <p className="text-slate-600 mt-1">
                                Be the first — <Link to="/projects/new" className="font-bold underline">post one</Link>.
                            </p>
                        </div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                        {projects.map((p, i) => {
                            const disc = disciplineMeta[p.discipline];
                            const meta = disc ? BRANCH_META[disc.branch] : BRANCH_META.business;
                            return (
                                <motion.div
                                    key={p.id || p.slug}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.02 }}
                                >
                                    <Link
                                        to={`/projects/${p.slug}`}
                                        className="crystal rounded-2xl p-5 block hover:scale-[1.01] transition-transform group"
                                        data-testid={`project-card-${p.slug}`}
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                                                {disc?.name || p.discipline}
                                            </span>
                                            {p.is_user_created && (
                                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                                    Community
                                                </span>
                                            )}
                                            <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                                {p.org}
                                            </span>
                                        </div>
                                        <h3 className="font-display font-bold text-xl text-slate-900 tracking-tight mt-1.5 group-hover:text-humanities transition-colors">
                                            {p.title}
                                        </h3>
                                        <p className="text-sm text-slate-600 mt-1 line-clamp-3">{p.summary}</p>
                                        <div className="mt-3 flex items-center justify-between text-xs">
                                            <span className="text-slate-500">
                                                Lead: <span className="font-bold text-slate-800">{p.lead_name}</span>
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
